import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      localStorage.setItem('hts_token', res.data.token);
      localStorage.setItem('hts_user', JSON.stringify(res.data.user));
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#24135F] flex items-center justify-center px-4 hero-noise relative">
      <div className="w-full max-w-md relative z-10">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-10 w-10 rounded-full bg-[#E4002B]/10 flex items-center justify-center">
              <Lock className="h-4 w-4 text-[#E4002B]" />
            </span>
            <div>
              <h1 className="font-heading text-2xl tracking-wider text-[#24135F]">ADMIN LOGIN</h1>
              <p className="text-xs text-[#53565A]/70">Higgins staff access only</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="login-email" className="text-xs font-semibold text-[#53565A]">
                Email
              </Label>
              <Input
                id="login-email"
                type="email"
                data-testid="admin-login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@higginsservices.com.au"
                required
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label htmlFor="login-password" className="text-xs font-semibold text-[#53565A]">
                Password
              </Label>
              <Input
                id="login-password"
                type="password"
                data-testid="admin-login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" role="alert" className="text-sm text-[#E4002B] font-medium">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-12 font-bold tracking-wider transition-colors duration-150"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </Button>
          </form>
        </div>
        <p className="text-center mt-6">
          <Link to="/" className="text-white/50 text-xs hover:text-white transition-colors duration-150">
            ← Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
